import React from 'react';
import { format } from 'date-fns';
import type { TimeRecordListItem } from '../../types/timeRecord';
import { formatTimeDisplay } from '../../utils/timeFormatting';
import type { UserProfileMap } from '../../types/user';

interface TimeRecordItemProps {
  record: TimeRecordListItem;
  onEdit: (date: string) => void;
  onDelete: (date: string) => void;
  userProfiles: UserProfileMap;
}

const TimeRecordItem: React.FC<TimeRecordItemProps> = ({ 
  record, 
  onEdit, 
  onDelete,
  userProfiles 
}) => {
  const displayName = userProfiles[record.user_id] || record.user_id;

  const renderTimePair = (label: string, timeIn: string | null, timeOut: string | null, icon: React.ReactNode) => {
    if (!timeIn && !timeOut) return null;
    return (
      <div className="flex items-center gap-2 py-1.5">
        <div className="flex-shrink-0 w-24 flex items-center gap-2">
          {icon}
          <span className="text-xs font-medium text-gray-600">{label}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-gray-900">
            {timeIn ? formatTimeDisplay(timeIn) : '-'} - {timeOut ? formatTimeDisplay(timeOut) : '-'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-gray-300 transition-colors duration-200">
      <div className="p-4">
        {/* Header section remains the same */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {format(new Date(record.record_date), 'EEEE, MMMM d, yyyy')}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{displayName}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(record.record_date)}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
              title="Edit record"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
            <button
              onClick={() => onDelete(record.record_date)}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
              title="Delete record"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Time records section */}
        <div className="space-y-1 border-t border-gray-100 pt-3">
          {renderTimePair('Morning', record.am_in, record.am_out, 
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
            </svg>
          )}
          {renderTimePair('Afternoon', record.pm_in, record.pm_out,
            <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm1-6a1 1 0 10-2 0v2a1 1 0 00.553.894l1.5.866a1 1 0 001-1.732L11 10V8z" clipRule="evenodd" />
            </svg>
          )}
          {renderTimePair('Overtime', record.ot_in, record.ot_out,
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
          )}
        </div>

        {/* Amounts section */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="space-y-2">
            {record.sales_amount !== null && record.sales_amount > 0 && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  Sales Amount: ₱{record.sales_amount.toFixed(2)}
                </span>
              </div>
            )}
            {record.ca_amount !== null && record.ca_amount > 0 && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  CA Amount: ₱{record.ca_amount.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Comments section */}
        {record.comments && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-gray-900">Comments</span>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-wrap pl-6">{record.comments}</p>
          </div>
        )}

        {/* Total hours section - Always show if there are any time entries */}
        {(record.am_in || record.pm_in || record.ot_in) && (
          <div className="mt-4 pt-3 border-t border-gray-100 bg-gradient-to-r from-blue-50 to-blue-100 -mx-4 -mb-4 p-4 rounded-b-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">Total Hours</span>
              <span className="text-lg font-semibold text-blue-600">
                {record.total_hours} hrs
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeRecordItem;